/**
 * The Metal Market Intelligence section of the Metal Resources landing (`/metal-resources`):
 * a banner, a description, one tile per blog category linking to
 * `/metal-resources/metal-market-intelligence/<slug>`, and a button link to the blog landing.
 *
 * Its only caller is `components/MetalResources/MetalResourcesLandingRyerson.tsx`, which holds
 * the copy (`MarketIntelligenceContent`) and fills `blogCategories` from the categories that
 * `src/pages/metal-resources/index.tsx` fetches with
 * `src/contentful/blog/getContentfulBlogCategoryEntries.tsx`. This file only switches between
 * `./MetalMarketIntelligenceDesktop/` and `./MetalMarketIIntelligenceMobile/` (the folder name
 * has a double `I`) at the `lg` breakpoint.
 */
import React from "react";
import { Media } from "@ryerson/frontend.next";
import MetalMarketIntelligenceDesktop from "./MetalMarketIntelligenceDesktop/MetalMarketIntelligenceDesktop";
import MetalMarketIntelligenceMobile from "./MetalMarketIIntelligenceMobile/MetalMarketIntelligenceMobile";

import { BlogCategoryMin } from "@contentful/blog/types";

/** The section's copy for one language, plus the category tiles. */
export type MetalMarketIntelligenceProps = {
	/** Desktop banner heading; the mobile image's `alt`. */
	title: string;
	/** Banner image, on desktop and on mobile. */
	imageUrl: string;
	/** Not read by either view: mobile also shows `imageUrl`. */
	mobileImageUrl: string;
	/** Mobile only: text over the banner image; `\n` breaks the line. */
	mobileImageText: string;
	description: string;
	/** The button link's label, one line per element. */
	actionText: string[];
	actionLink: string;
	/** Categories in `ordinal` order, one tile each. */
	blogCategories:  BlogCategoryMin[];
};

/** Renders the desktop or mobile view of the section by breakpoint. */
const MetalMarketIntelligence: React.FC<
	MetalMarketIntelligenceProps 
> = ({ blogCategories, ...props }) => {
	return (
		<>
			<Media greaterThanOrEqual="lg">
				<MetalMarketIntelligenceDesktop {...props} blogCategories={blogCategories} />
			</Media>
			<Media lessThan="lg">
				<MetalMarketIntelligenceMobile {...props} blogCategories={blogCategories} />
			</Media>
		</>
	);
};

export default MetalMarketIntelligence;
